import clsx from "clsx";
import { useMemo } from "react";
import useSWR from "swr";
import { Skeleton } from "antd-mobile";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Token } from "~/types";
import {
    formatMarketCap,
    formatPercent,
    formatPrice,
    formatVolume,
} from "~/utils/number-format";
import { TokenBadge } from "../TokenBadge/TokenBadge";
import { TokenIconSize } from "../TokenIcon";
import { getBuySellColor } from "~/pages/utils";
import { isBuiltInToken } from "~/pages/Trade/utils";
import { entries, first, isEmpty } from "lodash-es";
import { SuggestionAmountsMap } from "~/pages/Trade/hooks/useSuggectionAmounts";
import { fetchTokenMarket } from "~/api/endpoints";
import { hapticFeedback } from "@telegram-apps/sdk-react";

type TokenProfileProps = {
    token: Token;
    onTokenClick: (token: Token) => void;
};

function KvDesc({ k = "", v = "", className = "", isLoading = false }) {
    return (
        <div
            className={clsx(
                "flex items-center text-body4 justify-between",
                className
            )}
        >
            <div className="text-text_2 font-Regular whitespace-nowrap">
                {k}
            </div>
            {isLoading ? (
                <Skeleton className="w-12 h-4 rounded" />
            ) : (
                <div className="text-text_1 whitespace-nowrap text-right">
                    {v}
                </div>
            )}
        </div>
    );
}

const TokenProfile: React.FC<TokenProfileProps> = ({ token, onTokenClick }) => {
    const { t } = useTranslation();
    const chain_id = String(token.token_base.chain_id);
    const contract = token.token_base.contract;
    const isBuiltIn = isBuiltInToken(contract);

    const initTokenData = (args: any) => {
        const [, chain_id, contract] = args;
        return fetchTokenMarket(chain_id, contract).then(
            (res) => res?.data || {}
        );
    };

    const { data: tokenInfo, isLoading } = useSWR(
        ["k-detail:fetchTokenMarket", chain_id, contract],
        initTokenData
    );

    const quickBuyItems = useMemo(() => {
        let quickBuyItems: Array<{
            text: string;
            symbol: string;
            contract: string;
            chain_id: string;
            from_amount: string;
        }> = [];
        if (!isBuiltIn) {
            const tokens = SuggestionAmountsMap[String(chain_id)];
            if (tokens) {
                const firstMap = first(entries(tokens));
                if (firstMap) {
                    const [symbol, amounts] = firstMap;
                    quickBuyItems = amounts.map((item) => ({
                        text: `${item} ${symbol}`,
                        symbol: symbol,
                        contract: contract,
                        chain_id: chain_id,
                        from_amount: String(item),
                    }));
                }
            }
        }

        return quickBuyItems;
    }, [chain_id, contract, isBuiltIn]);

    return (
        <div className="px-4 cursor-pointer">
            <div
                className="flex items-center justify-between py-3.5"
                onClick={() => onTokenClick(token)}
            >
                <div className="flex items-center">
                    <TokenBadge
                        size={TokenIconSize.size40}
                        tailIconSize={TokenIconSize.mini}
                        tailIconClass="!border-[1.5px]"
                        icon={token.token_base.logo}
                        tokenIconBorder={true}
                        chainIcon={token.token_base.chain_logo}
                        symbol={token.token_base.symbol}
                        chainName={token.token_base.chain_name}
                    />
                    <div className="ml-[10px] flex flex-col">
                        <div className="font-Semibold text-text_1 text-body1 whitespace-nowrap">
                            {token.token_base.symbol}
                        </div>
                        <div className="text-direction1 font-semibold text-text_2 whitespace-nowrap mt-[2px]">
                            {token.token_base.chain_name}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-end">
                    <div className="font-Semibold text-text_1 text-body1 whitespace-nowrap">
                        {formatPrice(token.token_market.price)}
                    </div>
                    <div
                        className={clsx(
                            "text-direction1 font-semibold whitespace-nowrap mt-[2px]",
                            getBuySellColor(token.token_market.change)
                        )}
                    >
                        {formatPercent(token.token_market.change)}
                    </div>
                </div>
            </div>

            <div onClick={() => onTokenClick(token)}>
                <KvDesc
                    k={t("coin_info_pop_market_market")}
                    v={formatMarketCap(
                        tokenInfo?.token_market?.market_cap ?? 0
                    )}
                    isLoading={isLoading}
                />
                <KvDesc
                    k={t("tvl")}
                    v={formatMarketCap(tokenInfo?.token_market?.tvl_usd ?? 0)}
                    className="mt-[6px]"
                    isLoading={isLoading}
                />
                <KvDesc
                    k={t("vol24")}
                    v={formatVolume(tokenInfo?.token_market?.volume_value ?? 0)}
                    className="mt-[6px]"
                    isLoading={isLoading}
                />
            </div>
            {!isEmpty(quickBuyItems) && (
                <>
                    <hr className="border-t-[0.5px] border-border_2 my-3" />
                    <h3 className="text-text_2 text-body4">
                        {t("fast_purchase")}
                    </h3>
                    <div className="flex items-center gap-2 mt-2">
                        {quickBuyItems.map((item, index) => {
                            return (
                                <Link
                                    key={index}
                                    to={`/quick-buy?chain_id=${item.chain_id}&contract=${item.contract}&from_amount=${item.from_amount}`}
                                    state={{ type: "inner" }}
                                    className="flex-1 h-8 bg-text_buy flex items-center rounded px-1.5 relative text-white"
                                    onClick={() =>
                                        hapticFeedback.impactOccurred("heavy")
                                    }
                                >
                                    <span className="flex-1 flex justify-center text-body3 font-semibold">
                                        {item.text}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default TokenProfile;
